import React, { useState, useContext } from "react";
import { AccountContext } from "../../store/AccountContext";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Radio,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import usePromise from "react-use-promise";
import { isBrowser } from "react-device-detect";
import {
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { Link } from "react-router-dom";

import visa from "payment-icons/min/flat/visa.svg";
import mastercard from "payment-icons/min/flat/mastercard.svg";
import amex from "payment-icons/min/flat/amex.svg";
import discover from "payment-icons/min/flat/discover.svg";
import useLogger from "../../hooks/useLogger";
import { CurrencySymbol } from "@booyaltd/core";
import { v4 as uuidv4 } from "uuid";

/**
 * PM5 T2 (money path, batch "myaccount-membership") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * PAYMENTS + SUBSCRIPTIONS ESCALATION CLASS — this is the "start membership"
 * card form (three Stripe Elements + the saved-card radio choice), and it
 * fires the `purchase` metric event. Styling-only: no handler, condition,
 * state value, prop or DOM node moved. `ELEMENT_OPTIONS` is Stripe's own
 * iframe styling API (it is NOT CSS and cannot be reached from this document),
 * so it is left byte-for-byte alone. The Element mount nodes keep their v4 box
 * declarations verbatim, so each hosted iframe is measured against the same
 * box it was before.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `paymentContainerHeader`,
 * `cardElementContainer`, `cardInputError`, `responseText`, `inputFieldTight`,
 * `defaultPaymentMethodLabel` and `bodyTextSml` were declared but never
 * referenced from this file's JSX. `inputLabel` was an empty object — JSS
 * emitted an empty rule. The `<div className={classes.cardContainer}>`
 * referenced a key that does not exist in this file's `useStyles` object, so
 * it resolved to `className={undefined}` and applied nothing.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements / third-party components that carry a fixed
 * style. None of these accept an `sx` prop, so `styled()` is the port.
 * ---------------------------------------------------------------------- */

// was: useStyles().cardPaymentContainer — a bare <form>.
const CardPaymentContainerForm = styled("form")(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

// was: useStyles().cardElementLabel — a bare <label>.
const CardElementLabel = styled("label")({
  color: "#656565",
  fontSize: "1rem",
  fontWeight: 500,
  fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
});

// was: useStyles().cardLogo — a bare <img>.
const CardLogoImg = styled("img")({
  width: "35px",
  maxWidth: "35px",
  marginLeft: "2px",
});

// was: useStyles().defaultPaymentMethodCardBrand — a bare <img>.
const CardBrandImg = styled("img")({
  width: "50px",
  maxWidth: "50px",
  paddingRight: "9px",
  paddingTop: "6px",
});

// was: useStyles().defaultPaymentMethodExpDate — a bare <span>.
const ExpDateSpan = styled("span")(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  fontWeight: 400,
  fontSize: "0.9rem",
}));

// was: useStyles().inputField, on the Stripe CardNumberElement / CardCvcElement
// mount nodes.
const CardNumberField = styled(CardNumberElement)({
  padding: "12px",
  border: "1px solid #a5a5a5",
  backgroundColor: "#f7f7f7",
  margin: "4px 0 15px 0",
  fontSize: "16px",
  borderRadius: "5px",
});

const CardCvcField = styled(CardCvcElement)({
  padding: "12px",
  border: "1px solid #a5a5a5",
  backgroundColor: "#f7f7f7",
  margin: "4px 0 15px 0",
  fontSize: "16px",
  borderRadius: "5px",
});

/**
 * was: `className={`${classes.inputField} ${classes.bottomPaymentCreds}`}` on
 * the Stripe CardExpiryElement mount node.
 *
 * Guide §2, "Two `makeStyles` keys on one element": both classes are
 * single-class specificity, so v4's winner was JSS sheet order — the order of
 * the keys in the `useStyles` object — and `bottomPaymentCreds` is declared
 * AFTER `inputField`. So the rendered box has `margin: 4px 0 0 0`, not
 * `4px 0 15px 0`. Reproduced by listing `margin` first and `marginBottom`
 * after it; do not "tidy" that order.
 */
const CardExpiryField = styled(CardExpiryElement)({
  padding: "12px",
  border: "1px solid #a5a5a5",
  backgroundColor: "#f7f7f7",
  margin: "4px 0 15px 0",
  fontSize: "16px",
  borderRadius: "5px",
  marginBottom: 0,
});

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ v4's `<Grid container spacing={2}>` was
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaces that with `gap` on the container and no padding on the items
 * (guide §2). Carry v4's own declarations verbatim: omit `spacing` (v9 default
 * 0 ⇒ `gap: 0px`) and give each item the 8px padding itself.
 */
const v4GridContainerSx = { width: "calc(100% + 16px)", margin: "-8px" };
const v4GridItemSx = { padding: "8px" };

/**
 * ♦ Guide §2, "Grid without a container". Several `<Grid item>`s here sit
 * inside a `<form>` or inside another Grid ITEM, and this component's own root
 * `<Grid item>` is rendered into `MembershipProcess`'s Grid item — none of them
 * has a Grid CONTAINER as its direct parent, and v9 publishes
 * `--Grid-parent-columns` only from a container's `> *` rule, so the `size`
 * `calc()` would be invalid. Ported as Boxes carrying the literal v4
 * declarations. Every orphan here is `xs={12}` (some with `sm`/`md` 12, the
 * same 100%), so one unconditional set reproduces every breakpoint.
 * (`.MuiGrid-item`'s `margin: 0` is a div's initial value, so it is not
 * restated — same as `Footer.js`.)
 */
const orphanGridItemFullSx = {
  boxSizing: "border-box",
  flexGrow: 0,
  flexBasis: "100%",
  maxWidth: "100%",
};

/**
 * ♦ NEW DELTA (guide §2). v4's `.MuiGrid-container` declared `width: 100%`;
 * v9's Grid container has NO width rule at all — `generateGridStyles` emits
 * only `display: flex` / `flex-wrap` / `gap`. Harmless for a block-level
 * container, but a container nested DIRECTLY inside another container is a
 * flex item, and `width: auto` makes it shrink-to-fit. Measured in Chromium
 * on the exact emitted CSS: 800px in v4, 32px in v9, restored to 800px by
 * this rule. Restated verbatim on every converted container.
 */
const v4GridContainerWidthSx = { width: "100%" };

// was: the root `<Grid item xs sm md className={classes.paymentContainer}>` —
// an orphan Grid item (above) merged with `paymentContainer`. The v4 Grid
// classes came from MUI's own JSS sheet and this file's class from a later
// one, so the file's rules go last; here the two sets are disjoint.
const paymentContainerSx = (theme) => ({
  ...orphanGridItemFullSx,
  marginTop: theme.spacing(2),
  boxShadow: "none",
  textAlign: "left",
});

// was: useStyles().paymentInformation
const paymentInformationItemSx = (theme) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up("sm")]: {
    marginBottom: 0,
  },
});

// was: useStyles().paymentInformationTitle. The literal `@media (min-width:600px)`
// becomes `breakpoints.up("sm")`; `themeV5` pins that to the v4 value (600), so
// the emitted query is identical.
const paymentInformationTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.2rem",
  lineHeight: "1.6rem",
  marginBottom: "0px",
  fontWeight: 500,
  fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
  [theme.breakpoints.up("sm")]: {
    marginBottom: "20px",
  },
});

// was: useStyles().bodyText
const bodyTextSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  marginBottom: theme.spacing(2),
  "& span": {
    fontWeight: 500,
  },
});

// was: useStyles().validationError
const validationErrorSx = (theme) => ({ margin: theme.spacing(2, 0) });

/**
 * was: `<Box display="flex" alignItems="center" className={classes.
 * defaultPaymentMethodContainer}>` (and a second use with `display="flex"`
 * only). Guide §2, "`makeStyles` `className` alongside `Box` system props":
 * the ex-system-props go FIRST and the ex-`className` rules after them, which
 * is the order v4 resolved them in.
 */
const defaultPaymentMethodContainerSx = (theme) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: "#f5f5f5",
  padding: theme.spacing(1.2),
  marginBottom: theme.spacing(0.5),
  "&:hover": {
    backgroundColor: "#fff",
  },
});

// The second use had no `alignItems` system prop.
const defaultPaymentMethodContainerNoAlignSx = (theme) => ({
  display: "flex",
  backgroundColor: "#f5f5f5",
  padding: theme.spacing(1.2),
  marginBottom: theme.spacing(0.5),
  "&:hover": {
    backgroundColor: "#fff",
  },
});

// was: useStyles().defaultPaymentCardNumber
const defaultPaymentCardNumberSx = (theme) => ({
  textAlign: "left",
  color: theme.palette.background.ffNavy,
  fontWeight: 500,
});

// was: useStyles().defaultPaymentMethodText
const defaultPaymentMethodTextSx = (theme) => ({
  textAlign: "left",
  color: theme.palette.background.ffNavy,
});

// was: useStyles().defaultPaymentMethodChange
const defaultPaymentMethodChangeSx = (theme) => ({
  color: theme.palette.background.ffGreen,
  fontWeight: 500,
  textDecoration: "underline",
  "&:hover": {
    cursor: "pointer",
  },
});

// was: useStyles().termsLabel
const termsLabelSx = (theme) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  color: "#656565",
  fontSize: "0.8rem",
  lineHeight: "1rem",
  textAlign: "left",
  [theme.breakpoints.up("sm")]: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontSize: "0.8rem",
    lineHeight: "1rem",
  },
  [theme.breakpoints.up("md")]: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontSize: "0.8rem",
    lineHeight: "1rem",
  },
  "& a": {
    color: "#656565",
    fontWeight: 500,
  },
});

// was: useStyles().midPaymentCreds, merged with the Grid item padding above
// (guide §2 — `Grid`'s `sx` has no array form, so the layers merge into one
// object; the two rules share no property, so order is not load-bearing here).
const midPaymentCredsItemSx = (theme) => ({
  padding: "8px",
  marginTop: "4px",
  [theme.breakpoints.up("sm")]: {
    marginTop: 0,
  },
});

// was: useStyles().paymentButton, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted `default` and
// made `primary` the default — and this theme's `primary.main` IS the lime that
// `typography.button` paints the button background with, so an uncompensated
// port renders an invisible lime-on-lime "Start Membership" button.
const paymentButtonSx = (theme) => ({
  width: "100%",
  margin: theme.spacing(2, 0, 0, 0),
  [theme.breakpoints.up("sm")]: {
    width: "auto",
  },
  color: "text.primary",
});

// was: useStyles().sectionDivider
const sectionDividerSx = {
  height: "1px",
  backgroundColor: "#3c3b3b",
  margin: "2% 0",
};

// was: useStyles().footerDivider
const footerDividerSx = {
  height: "1px",
  backgroundColor: "#3c3b3b",
  margin: "5% 0 2% 0",
};

const CardPaymentForm = ({ Subscription }) => {
  let renewalDate = new Date();

  if (Subscription.entitlementPeriodUnit === "Year") {
    renewalDate = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    }).format(
      new Date(
        renewalDate.setFullYear(
          renewalDate.getFullYear() + Subscription.entitlementPeriodValue,
        ),
      ),
    );
  } else if (Subscription.entitlementPeriodUnit === "Month") {
    renewalDate = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    }).format(
      new Date(
        renewalDate.setMonth(
          renewalDate.getMonth() + Subscription.entitlementPeriodValue,
        ),
      ),
    );
  } else if (Subscription.entitlementPeriodUnit === "Day") {
    renewalDate = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    }).format(
      new Date(
        renewalDate.setMonth(
          renewalDate.getMonth() + Subscription.entitlementPeriodValue,
        ),
      ),
    );
  }

  const stripe = useStripe();
  const elements = useElements();
  const { logMetricEvent } = useLogger();

  const ELEMENT_OPTIONS = {
    style: {
      base: {
        fontSize: "16px",
        "::placeholder": {
          color: "#ccc",
        },
      },
      invalid: {
        color: "#b92020",
      },
    },
  };

  const CardForm = () => {
    return (
      <CardPaymentContainerForm onSubmit={handleStripeSubmit}>
        <Divider sx={sectionDividerSx} />
        <Box sx={orphanGridItemFullSx}>
          <Typography variant="body1" sx={bodyTextSx}>
            Please enter your card details below.
          </Typography>
          {paymentError && (
            <>
              {paymentErrorResponse !== "undefined" && (
                <Alert sx={validationErrorSx} severity="error">
                  {paymentErrorResponse}
                </Alert>
              )}
            </>
          )}
          {cardInputError && (
            <>
              {cardInputError !== "undefined" && (
                <Alert sx={validationErrorSx} severity="error">
                  {cardInputError}
                </Alert>
              )}
            </>
          )}
        </Box>
        <Box sx={orphanGridItemFullSx}>
          {/* Post cutover - split the CardElement down */}
          <CardElementLabel htmlFor="cardNumber">Card Number</CardElementLabel>
          <CardNumberField
            id="cardNumber"
            onFocus={clearErrors}
            options={ELEMENT_OPTIONS}
          />
        </Box>
        <Grid container sx={v4GridContainerSx}>
          <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={midPaymentCredsItemSx}>
            <CardElementLabel htmlFor="expiry">
              Card Expiration
            </CardElementLabel>
            <CardExpiryField
              id="expiry"
              onFocus={clearErrors}
              options={ELEMENT_OPTIONS}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={v4GridItemSx}>
            <CardElementLabel htmlFor="cvc">CVC</CardElementLabel>
            <CardCvcField
              id="cvc"
              onFocus={clearErrors}
              options={ELEMENT_OPTIONS}
            />
          </Grid>
        </Grid>
        <Typography variant="body1" sx={termsLabelSx}>
          By {isBrowser && <>clicking</>}
          {!isBrowser && <>tapping</>} on &apos;Start Membership&apos; button
          below, you agree to FarmFLiX&apos;s{" "}
          <Link to="/terms">Terms and Conditions</Link> and{" "}
          <Link to="/privacy-policy">Privacy Policy</Link>. Your next membership
          payment will be taken on <b>{renewalDate}</b>. FarmFLiX will
          automatically track and charge your membership fee (
          {CurrencySymbol({ currency: Subscription.currency })}
          {Subscription.cost}/{Subscription.entitlementPeriodUnit}) until you
          cancel your membership.
        </Typography>
        <Box sx={orphanGridItemFullSx} align="center">
          <Button
            type="submit"
            sx={paymentButtonSx}
            disabled={!stripe || !Subscription.id || submittingButton}
          >
            Start Membership
          </Button>
        </Box>
      </CardPaymentContainerForm>
    );
  };

  const {
    createStandardSubscriptionStripe,
    paymentErrorResponse,
    paymentError,
    resetErrors,
    getMembershipDetails,
  } = useContext(AccountContext);

  const [reloadTrigger, setReloadTrigger] = useState(Date.now());
  const [membershipDetails, , state] = usePromise(getMembershipDetails, [
    reloadTrigger,
  ]);

  const loading = state === "pending";

  // eslint-disable-next-line
  const reload = () => setReloadTrigger(Date.now());

  const [cardInputError, setCardInputError] = useState(null);
  const [submittingButton, setSubmittingButton] = useState(false);

  const [selectedValue, setSelectedValue] = useState("default");

  const handleChange = (event) => {
    clearErrors();
    setSelectedValue(event.target.value);
  };

  const clearErrors = (event) => {
    setSubmittingButton(false);
    setCardInputError("");
    resetErrors();
  };

  // #659: the eventId minted at submit rides the createPayment request (the
  // server fires the CAPI Purchase) and pairs this browser pixel event with
  // it for Meta dedup, should the success callback ever be invoked.
  const onPaymentSuccess = (product, eventId) => (payment) => {
    logMetricEvent(
      {
        type: "purchase",
        payload: {
          userId: payment.memberId,
          paymentId: payment.id,
          id: product.id || "",
          memberStatusId: product.memberStatusId || "",
          name: product.name || "",
          cost: parseFloat(product.cost || "0"),
          currency: product.currency || "",
          stripeReference: product.stripeReference || "",
        },
      },
      eventId,
    );
  };

  // Stripe Membership Submission
  const startNewMembership = async () => {
    const productId = Subscription.id;

    // Passing "default" in to use the current payment method on the Stripe Account
    const stripePaymentToken = "default";
    const eventId = uuidv4();

    await createStandardSubscriptionStripe(
      {
        productId,
        stripePaymentToken,
        eventId,
      },
      onPaymentSuccess(Subscription, eventId),
    );
  };

  // Stripe (Card) Submission
  const handleStripeSubmit = async (event) => {
    // Block double click/tap on payment button
    setSubmittingButton(true);

    // Block native form submission.
    event.preventDefault();

    if (!stripe || !elements) {
      // form submission until Stripe.js has loaded.
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    const payload = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    const token = await stripe.createToken(cardElement);

    if (payload.error) {
      setCardInputError(payload.error.message);
      setSubmittingButton(false);
      return;
    } else {
      setCardInputError(null);
      const productId = Subscription.id;
      const stripePaymentToken = token.token.id;
      const eventId = uuidv4();

      // eslint-disable-next-line
      const paymentAttempt = await createStandardSubscriptionStripe(
        {
          productId,
          stripePaymentToken,
          eventId,
        },
        onPaymentSuccess(Subscription, eventId),
      );
    }
  };

  return (
    <>
      <Box sx={paymentContainerSx}>
        <div>
          <Grid container sx={v4GridContainerWidthSx}>
            <Grid
              size={{ xs: 12, sm: 12, md: 12 }}
              sx={paymentInformationItemSx}
            >
              <Box sx={{ display: "flex" }}>
                <Box style={{ flex: 1 }}>
                  <Typography sx={paymentInformationTitleSx} variant="body1">
                    Payment Information
                  </Typography>
                </Box>
                <Box style={{ textAlign: "right" }}>
                  <CardLogoImg src={visa} alt="Visa" />
                  <CardLogoImg src={mastercard} alt="Mastercard" />
                  <CardLogoImg src={amex} alt="American Express" />
                  <CardLogoImg src={discover} alt="Discover" />
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
              {!loading && (
                <>
                  {membershipDetails && (
                    <>
                      {membershipDetails.cardBrand &&
                        membershipDetails.creditCard && (
                          <>
                            <Typography variant="body1" sx={bodyTextSx}>
                              Pay securely with your card or enter a new card.
                            </Typography>
                            {paymentError && (
                              <>
                                {paymentErrorResponse !== "undefined" && (
                                  <Alert
                                    sx={validationErrorSx}
                                    severity="error"
                                    variant="filled"
                                  >
                                    {paymentErrorResponse}
                                  </Alert>
                                )}
                              </>
                            )}

                            <Box sx={defaultPaymentMethodContainerSx}>
                              <Box>
                                {/* ♦ v9 removed `inputProps` from SwitchBase
                                    (Radio/Checkbox/Switch) — it now lands in
                                    `...other` and is spread onto the ROOT span,
                                    silently dropping the aria-label. The
                                    replacement is `slotProps.input`, which
                                    reaches the same <input> v4's `inputProps`
                                    did. */}
                                <Radio
                                  color="primary"
                                  checked={selectedValue === "default"}
                                  onChange={handleChange}
                                  value="default"
                                  name="radio-button-demo"
                                  slotProps={{
                                    input: { "aria-label": "Default" },
                                  }}
                                />
                              </Box>
                              <Box sx={{ flex: "1", alignItems: "center" }}>
                                <Typography
                                  variant="body1"
                                  sx={defaultPaymentCardNumberSx}
                                >
                                  **** **** **** {membershipDetails.creditCard}
                                  <br />
                                  <ExpDateSpan>
                                    Exp: {membershipDetails.cardExpMonth} /{" "}
                                    {membershipDetails.cardExpYear}
                                  </ExpDateSpan>
                                </Typography>
                              </Box>
                              <Box sx={{ alignItems: "center" }}>
                                <Typography
                                  variant="body1"
                                  sx={defaultPaymentMethodChangeSx}
                                >
                                  {membershipDetails.cardBrand === "visa" && (
                                    <CardBrandImg alt="Visa" src={visa} />
                                  )}
                                  {membershipDetails.cardBrand ===
                                    "mastercard" && (
                                    <CardBrandImg
                                      alt="Mastercard"
                                      src={mastercard}
                                    />
                                  )}
                                  {membershipDetails.cardBrand === "amex" && (
                                    <CardBrandImg
                                      alt="American Express"
                                      src={amex}
                                    />
                                  )}
                                  {membershipDetails.cardBrand ===
                                    "discover" && (
                                    <CardBrandImg
                                      alt="Discover"
                                      src={discover}
                                    />
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={defaultPaymentMethodContainerNoAlignSx}>
                              <Box sx={{ alignItems: "center" }}>
                                <Radio
                                  color="primary"
                                  checked={selectedValue === "new"}
                                  onChange={handleChange}
                                  value="new"
                                  name="radio-button-demo"
                                  slotProps={{
                                    input: { "aria-label": "New" },
                                  }}
                                />
                              </Box>
                              <Box sx={{ alignItems: "center" }}>
                                <Typography
                                  variant="body1"
                                  sx={defaultPaymentMethodTextSx}
                                  style={{ padding: "10px 0" }}
                                >
                                  Use a new card
                                </Typography>
                              </Box>
                            </Box>
                            {selectedValue === "default" && (
                              <>
                                <Typography variant="body1" sx={termsLabelSx}>
                                  By {isBrowser && <>clicking</>}
                                  {!isBrowser && <>tapping</>} on &apos;Start
                                  Membership&apos; button below, you agree to
                                  FarmFLiX&apos;s{" "}
                                  <Link to="/terms">Terms and Conditions</Link>{" "}
                                  and{" "}
                                  <Link to="/privacy-policy">
                                    Privacy Policy
                                  </Link>
                                  . Your next membership payment will be taken
                                  on <b>{renewalDate}</b>. FarmFLiX will
                                  automatically track and charge your membership
                                  fee (
                                  {CurrencySymbol({
                                    currency: Subscription.currency,
                                  })}
                                  {Subscription.cost}/
                                  {Subscription.entitlementPeriodUnit}) until
                                  you cancel your membership.
                                </Typography>
                                <Box sx={orphanGridItemFullSx} align="center">
                                  <Button
                                    onClick={startNewMembership}
                                    sx={paymentButtonSx}
                                    disabled={
                                      !stripe ||
                                      !Subscription.id ||
                                      submittingButton
                                    }
                                  >
                                    Start Membership
                                  </Button>
                                </Box>
                              </>
                            )}
                            {selectedValue === "new" && (
                              <>
                                <CardPaymentContainerForm
                                  onSubmit={handleStripeSubmit}
                                >
                                  <Box sx={orphanGridItemFullSx}>
                                    <Typography variant="body1" sx={bodyTextSx}>
                                      Please enter your card details below.
                                    </Typography>
                                    {cardInputError && (
                                      <>
                                        {cardInputError !== "undefined" && (
                                          <Alert
                                            sx={validationErrorSx}
                                            severity="error"
                                            variant="filled"
                                          >
                                            {cardInputError}
                                          </Alert>
                                        )}
                                      </>
                                    )}
                                  </Box>
                                  <Box sx={orphanGridItemFullSx}>
                                    <CardElementLabel htmlFor="cardNumber">
                                      Card Number
                                    </CardElementLabel>
                                    <CardNumberField
                                      id="cardNumber"
                                      onFocus={clearErrors}
                                      options={ELEMENT_OPTIONS}
                                    />
                                  </Box>
                                  <Grid container sx={v4GridContainerSx}>
                                    <Grid
                                      size={{ xs: 12, sm: 6, md: 6 }}
                                      sx={midPaymentCredsItemSx}
                                    >
                                      <CardElementLabel htmlFor="expiry">
                                        Card Expiration
                                      </CardElementLabel>
                                      <CardExpiryField
                                        id="expiry"
                                        onFocus={clearErrors}
                                        options={ELEMENT_OPTIONS}
                                      />
                                    </Grid>
                                    <Grid
                                      size={{ xs: 12, sm: 6, md: 6 }}
                                      sx={v4GridItemSx}
                                    >
                                      <CardElementLabel htmlFor="cvc">
                                        CVC
                                      </CardElementLabel>
                                      <CardCvcField
                                        id="cvc"
                                        onFocus={clearErrors}
                                        options={ELEMENT_OPTIONS}
                                      />
                                    </Grid>
                                  </Grid>
                                  <Typography variant="body1" sx={termsLabelSx}>
                                    By {isBrowser && <>clicking</>}
                                    {!isBrowser && <>tapping</>} on &apos;Start
                                    Membership&apos; button below, you agree to
                                    FarmFLiX&apos;s{" "}
                                    <Link to="/terms">
                                      Terms and Conditions
                                    </Link>{" "}
                                    and{" "}
                                    <Link to="/privacy-policy">
                                      Privacy Policy
                                    </Link>
                                    . Your next membership payment will be taken
                                    on <b>{renewalDate}</b>. FarmFLiX will
                                    automatically track and charge your
                                    membership fee (
                                    {CurrencySymbol({
                                      currency: Subscription.currency,
                                    })}
                                    {Subscription.cost}/
                                    {Subscription.entitlementPeriodUnit}) until
                                    you cancel your membership.
                                  </Typography>
                                  <Box sx={orphanGridItemFullSx} align="center">
                                    <Button
                                      type="submit"
                                      sx={paymentButtonSx}
                                      disabled={
                                        !stripe ||
                                        !Subscription.id ||
                                        submittingButton
                                      }
                                    >
                                      Start Membership
                                    </Button>
                                  </Box>
                                </CardPaymentContainerForm>
                              </>
                            )}
                          </>
                        )}
                      {!membershipDetails.cardBrand &&
                        !membershipDetails.creditCard && (
                          <>
                            <form onSubmit={handleStripeSubmit}>
                              <Box sx={orphanGridItemFullSx}>
                                <Typography variant="body1" sx={bodyTextSx}>
                                  Please enter your card details below.
                                </Typography>
                                {paymentError && (
                                  <>
                                    {paymentErrorResponse !== "undefined" && (
                                      <Alert
                                        sx={validationErrorSx}
                                        severity="error"
                                      >
                                        {paymentErrorResponse}
                                      </Alert>
                                    )}
                                  </>
                                )}
                                {cardInputError && (
                                  <>
                                    {cardInputError !== "undefined" && (
                                      <Alert
                                        sx={validationErrorSx}
                                        severity="error"
                                      >
                                        {cardInputError}
                                      </Alert>
                                    )}
                                  </>
                                )}
                              </Box>
                              <Box sx={orphanGridItemFullSx}>
                                {/* Post cutover - split the CardElement down */}
                                <CardElementLabel htmlFor="cardNumber">
                                  Card Number
                                </CardElementLabel>
                                <CardNumberField
                                  id="cardNumber"
                                  onFocus={clearErrors}
                                  options={ELEMENT_OPTIONS}
                                />
                              </Box>
                              <Grid container sx={v4GridContainerSx}>
                                <Grid
                                  size={{ xs: 12, sm: 6, md: 6 }}
                                  sx={midPaymentCredsItemSx}
                                >
                                  <CardElementLabel htmlFor="expiry">
                                    Card Expiration
                                  </CardElementLabel>
                                  <CardExpiryField
                                    id="expiry"
                                    onFocus={clearErrors}
                                    options={ELEMENT_OPTIONS}
                                  />
                                </Grid>
                                <Grid
                                  size={{ xs: 12, sm: 6, md: 6 }}
                                  sx={v4GridItemSx}
                                >
                                  <CardElementLabel htmlFor="cvc">
                                    CVC
                                  </CardElementLabel>
                                  <CardCvcField
                                    id="cvc"
                                    onFocus={clearErrors}
                                    options={ELEMENT_OPTIONS}
                                  />
                                </Grid>
                              </Grid>
                              <Typography variant="body1" sx={termsLabelSx}>
                                By clicking/tapping on &apos;Start
                                Membership&apos; button below, you agree to
                                FarmFLiX&apos;s{" "}
                                <Link to="/terms">Terms and Conditions</Link>{" "}
                                and{" "}
                                <Link to="/privacy-policy">Privacy Policy</Link>
                                . Your next membership payment will be taken on{" "}
                                <b>{renewalDate}</b>. FarmFLiX will
                                automatically track and charge your membership
                                fee (
                                {CurrencySymbol({
                                  currency: Subscription.currency,
                                })}
                                {Subscription.cost}/
                                {Subscription.entitlementPeriodUnit}) until you
                                cancel your membership.
                              </Typography>
                              <Box sx={orphanGridItemFullSx} align="center">
                                <Button
                                  type="submit"
                                  sx={paymentButtonSx}
                                  disabled={
                                    !stripe ||
                                    !Subscription.id ||
                                    submittingButton
                                  }
                                >
                                  Start Membership
                                </Button>
                              </Box>
                            </form>
                          </>
                        )}
                    </>
                  )}
                  {!membershipDetails && (
                    <>
                      <CardForm />
                    </>
                  )}
                </>
              )}
              {loading && (
                <>
                  <Box sx={{ m: 5 }} align="center">
                    <CircularProgress variant="indeterminate"></CircularProgress>
                  </Box>
                </>
              )}
              <Divider sx={footerDividerSx} />
            </Grid>
          </Grid>
        </div>
      </Box>
    </>
  );
};

export default CardPaymentForm;
